import { makeStyles } from "@material-ui/core/styles";
import { tokens } from "../../theme/tokens";

export const useCaseStyles = makeStyles((theme) => ({
  caseContainer: {
    width: "98%",
    paddingLeft: "2%",
    [theme.breakpoints.down("md")]: {
      width: "90%",
    },
  },
  caseContainerTallScreen: {
    width: "98%",
    paddingLeft: "2%",
    paddingRight: "2%",
  },

  // The wizard reads as one surface: a sunken canvas holding a single bordered
  // panel, elevation from the border rather than a shadow (doctrine §10).
  pageWrap: {
    padding: tokens.space[4],
    backgroundColor: tokens.color.surface.sunken,
    minHeight: "100%",
  },
  panel: {
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    display: "flex",
    flexDirection: "column",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
    padding: `${tokens.space[3]} ${tokens.space[4]}`,
    borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  panelBody: {
    padding: tokens.space[4],
  },
  footerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.space[3],
    padding: `${tokens.space[3]} ${tokens.space[4]}`,
    borderTop: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  sectionTitle: {
    fontSize: tokens.type.heading.size,
    fontWeight: tokens.type.heading.weight,
    lineHeight: tokens.type.heading.lineHeight,
    color: tokens.color.text.primary,
    margin: 0,
  },

  formContainer: {
    paddingRight: "10px",
  },
  clInfo: {
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  header: {
    width: "100%",
    backgroundColor: tokens.color.brand.primaryAction,
    paddingTop: "5px",
    paddingBottom: "5px",
  },
  formFieldContainer: {
    backgroundColor: tokens.color.surface.default,
    padding: "10px",
  },

  // Field rows: label sits above its field (style guide §12), the two living
  // together (doctrine §9). Label is caption / secondary; the field fills.
  rowContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[1],
    marginTop: 0,
  },
  labelContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    "& p": {
      fontSize: tokens.type.caption.size,
      fontWeight: tokens.type.caption.weight,
      lineHeight: tokens.type.caption.lineHeight,
      color: tokens.color.text.secondary,
    },
  },
  fieldContainer: {
    width: "100%",
  },
  select: {
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    paddingLeft: "3px",
  },
  buttonRow: {
    width: "20%",
    paddingTop: "10px",
  },
  subjectFieldsRow: {
    width: "80%",
  },
  subjectInput: {
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    "& .MuiInputBase-input": {
      textIndent: "10px",
    },
    paddingLeft: "3px",
    "&:not(:first-child)": {
      marginTop: tokens.space[2],
    },
  },
  buttonContainer: {
    "&:not(:first-child)": {
      marginTop: tokens.space[2],
    },
  },
  primaryCheckbox: {
    flexDirection: "row-reverse",
    marginRight: tokens.space[5],
    "& .MuiTypography-body1": { color: tokens.color.text.primary },
    "& .MuiSvgIcon-root": { width: "1.3em", height: "1.3em" },
  },
  subjectIcon: { fill: tokens.color.brand.primary },
  subjectHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.space[3],
    marginTop: tokens.space[3],
    marginBottom: tokens.space[3],
  },
  subjectTypeField: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
  },

  // Subject selector: a segmented row of pills. The active one is the primary
  // action colour; the rest are quiet, bordered surfaces (style guide §9).
  subjectButton: {
    minWidth: "160px",
    height: tokens.density.rowComfortable,
    padding: `0 ${tokens.space[3]}`,
    textTransform: "capitalize",
    borderRadius: tokens.radius.sm,
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    boxShadow: "none",
  },
  subjectButtonActive: {
    backgroundColor: tokens.color.brand.primaryAction,
    color: tokens.color.brand.onPrimary,
    "&:hover": { backgroundColor: tokens.color.brand.primaryActionHover },
  },
  subjectButtonInactive: {
    backgroundColor: tokens.color.surface.default,
    color: tokens.color.brand.primaryAction,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    "&:hover": { backgroundColor: tokens.color.surface.hover },
  },
  addSubjectButton: {
    minWidth: "160px",
    height: tokens.density.rowComfortable,
    padding: `0 ${tokens.space[3]}`,
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    boxShadow: "none",
    color: tokens.color.brand.primaryAction,
    textTransform: "capitalize",
    borderRadius: tokens.radius.sm,
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    "&:hover": { backgroundColor: tokens.color.surface.hover },
  },
  plusButton: {
    color: tokens.color.brand.primary,
    cursor: "pointer",
    "&:hover": {
      opacity: "0.8",
    },
  },

  // Primary action (Next / Submit): brand fill, no border, no shadow, 40px.
  postButton: {
    backgroundColor: tokens.color.brand.primaryAction,
    fontFamily: tokens.type.family,
    height: tokens.density.field,
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.brand.onPrimary,
    textTransform: "none",
    borderRadius: tokens.radius.sm,
    boxShadow: "none",
    textAlign: "center",
    padding: `${tokens.space[2]} ${tokens.space[4]}`,
    minWidth: "100px",
    "&:hover": {
      backgroundColor: tokens.color.brand.primaryActionHover,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: tokens.color.surface.subtle,
      color: tokens.color.text.disabled,
    },
  },
  // Secondary action (Back): quiet bordered surface, never a second brand hue.
  backButton: {
    backgroundColor: tokens.color.surface.default,
    fontFamily: tokens.type.family,
    height: tokens.density.field,
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.text.primary,
    textTransform: "none",
    borderRadius: tokens.radius.sm,
    boxShadow: "none",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    padding: `${tokens.space[2]} ${tokens.space[4]}`,
    minWidth: "100px",
    "&:hover": {
      backgroundColor: tokens.color.surface.hover,
      boxShadow: "none",
    },
  },
  error: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.status.error,
    marginTop: tokens.space[1],
    paddingLeft: 0,
  },
  relativeClass: {
    [theme.breakpoints.up("md")]: {
      width: "100%",
      height: "100%",
      position: "relative",
    },
  },
  absoluteClass: {
    [theme.breakpoints.up("md")]: {
      position: "absolute",
      bottom: "0px",
      width: "100%",
    },
  },
  topBar: {
    height: "50px",
    padding: "0 2%",
    display: "flex",
    alignItems: "center",
  },
  parent: {
    justifyContent: "space-between",
    flexDirection: "row",
    flexWrap: "wrap",
    display: "flex",
    marginTop: tokens.space[3],
    height: "auto",
    "&:first-child": { marginTop: 0 },
  },
  // A row of two side-by-side columns with a consistent gutter.
  columns: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.space[5],
  },
  column: {
    flex: 1,
    minWidth: 0,
  },
  textField: {
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    paddingLeft: "3px",
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  },
  iconList: {
    marginLeft: "auto",
  },
  parentChild: {
    flex: "1",
    width: "100%",
    minWidth: 0,
  },
  parentChildTallScreen: {
    flex: "1",
    width: "100%",
    minWidth: 0,
  },
  input: {
    width: "100%",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    "& .MuiInputBase-input": {
      textIndent: "10px",
      textTransform: "capitalize",
      color: tokens.color.text.primary,
      fontSize: tokens.type.dense.size,
    },
    borderRadius: tokens.radius.sm,
    height: tokens.density.field,
    paddingLeft: "3px",
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  },
  emailInput: {
    width: "100%",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    "& .MuiInputBase-input": {
      textIndent: "10px",
      color: tokens.color.text.primary,
      fontSize: tokens.type.dense.size,
    },
    borderRadius: tokens.radius.sm,
    height: tokens.density.field,
    paddingLeft: "3px",
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  },
  dateInput: {
    padding: "0px 12px",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    "& .MuiInputBase-input": {
      textIndent: "6px",
      color: tokens.color.text.primary,
      fontSize: tokens.type.dense.size,
    },
    borderRadius: tokens.radius.sm,
    height: tokens.density.field,
    paddingLeft: "3px",
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
  },
  astrike: {
    color: tokens.color.status.error,
    fontWeight: 500,
  },
}));
